<script setup>
import { onMounted, watch, ref, computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { grupoUsuario_admins, grupoUsuario_analistaprojeto } from '@/commons/utils';
import { useBasicosProjetos, useProjeto } from '@/modules/projeto/composables/useProjeto';

const { verificarCoordenacao } = useBasicosProjetos();
const { projeto, carregarProjeto } = useProjeto();
const authStore = useAuthStore();

const props = defineProps({
    projetoId: {
        type: [String, Number],
        default: null
    },
    // Indica se o usuário logado pode criar/editar a solicitação (coordenador ou subcoordenador)
    modelValue: {
        type: Boolean,
        default: false
    },
    // Carregamento da view mãe — evita exibir o aviso antes dos dados estarem prontos
    carregamento: {
        type: Boolean,
        default: false
    },
    // Permite que o analista de projetos também crie/edite a solicitação
    permitirAnalista: {
        type: Boolean,
        default: false
    },
    // Situações do projeto em que a solicitação pode ser feita (vazio = qualquer situação)
    statusPermitidos: {
        type: Array,
        default: () => []
    }
});

const emit = defineEmits(['update:modelValue']);

const temPermissao = ref(true);
const statusValido = ref(true);
const verificando = ref(true);

const podeEditar = computed(() => temPermissao.value && statusValido.value);

const mensagemPermissao = computed(() => props.permitirAnalista
    ? 'Apenas o coordenador, os subcoordenadores e os analistas de projetos podem criar ou editar esta solicitação.'
    : 'Apenas o coordenador e os subcoordenadores do projeto podem criar ou editar solicitações de projeto.');

const verificar = async (projetoId) => {
    if (!projetoId) return;

    verificando.value = true;

    // Não importa isAnalistaProjeto de commons/permissoes: este componente é registrado
    // globalmente antes do Pinia, e aquele módulo usa a store no carregamento.
    const grupos = authStore.getUsuarioGrupos();
    const ehAnalista = grupos.includes(grupoUsuario_analistaprojeto) || grupos.includes(grupoUsuario_admins);

    temPermissao.value = (props.permitirAnalista && ehAnalista)
        || await verificarCoordenacao(projetoId, authStore.usuario.relacoes.pessoa_fisica_id);

    if (props.statusPermitidos.length > 0) {
        await carregarProjeto(projetoId);
        statusValido.value = props.statusPermitidos.includes(projeto.value?.projeto_status_ultimo);
    }

    emit('update:modelValue', podeEditar.value);
    verificando.value = false;
};

watch(() => props.projetoId, (novoProjetoId) => {
    verificar(novoProjetoId);
});

onMounted(() => {
    verificar(props.projetoId);
});
</script>

<template>
    <div v-if="!props.carregamento && !verificando && !podeEditar" class="mb-4 col-12">
        <div class="border-blue-300 bg-blue-50 border-1 p-3 border-round-md">
            <div class="flex align-items-center gap-3">
                <i class="pi pi-exclamation-circle text-blue-500"></i>
                <p v-if="!temPermissao" class="m-0 text-700 text-xs font-medium">{{ mensagemPermissao }}</p>
                <p v-else class="m-0 text-700 text-xs font-medium">
                    Esta solicitação só pode ser feita com o projeto na situação: {{ props.statusPermitidos.join(', ') }}.
                </p>
            </div>
        </div>
    </div>
</template>
